import axios from 'axios'
import renderRadioGroup from 'components/FormControls/radioGroup'
import renderSelect from 'components/FormControls/select'
import renderInputField from 'components/FormControls/textInput'
import { Button, Typography } from 'gu-ui-kit'
import omit from 'lodash/omit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useHistory } from 'react-router-dom'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import { useGetAccountAction } from 'modules/Services/hooks/useGetAccountAction'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import domainValidator from 'utils/validators/domainValidator'
import * as Yup from 'yup'
import oneOfMapper from 'utils/validators/oneOfMapper'
import SERVICES_API_V1 from 'api/services/v1'
import { ROUTES } from 'constants/routes'
import { useParams } from 'react-router'
import { YANDEX_SUBSCRIPTION_CAMPAIGN_CREATE } from 'modules/Services/types'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('common::validation_messages.required'),
  company: Yup.object()
    .shape({
      id: Yup.string().required('common::validation_messages.required'),
      country: Yup.string().required('common::validation_messages.required'),
    })
    .required('common::validation_messages.required'),
  product: Yup.string().oneOf(['SUBSCRIPTION', 'GEO', 'WEB'], ({ values }) =>
    oneOfMapper(
      values,
      'common::validation_messages.oneOf',
      'services::forms.campaign_create.fields.product.options.',
    ),
  ),
  url: Yup.string().when('product', {
    is: (val) => val === 'WEB',
    then: (schema) =>
      schema
        .test(
          'domainValidator',
          'common::validation_messages.incorrect_url',
          domainValidator,
        )
        .required('common::validation_messages.required'),
    otherwise: (schema) => schema.strip(),
  }),
})

let CampaignCreateForm = ({ handleSubmit, submitting, form }) => {
  const { t } = useTranslation()
  const selector = formValueSelector(form)
  const history = useHistory()
  const campaignsAction = useGetAccountAction('campaigns')
  const pathGen = useGeneratePathI18n()
  const { accountId, networkType } = useParams()

  let req = null
  let source
  const CancelToken = axios.CancelToken

  const { product } = useSelector((state) => ({
    product: selector(state, 'product'),
  }))

  const submit = useCallback(
    (values, dispatch) =>
      new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: YANDEX_SUBSCRIPTION_CAMPAIGN_CREATE,
          payload: {
            networkType: 'yandex-business',
            accountId,
            data: product !== 'WEB' ? { ...omit(values, ['url']) } : values,
            formName: form,
          },
        })
      }).then(() =>
        history.push(
          pathGen(campaignsAction.url, {
            networkType: 'yandex-business',
            accountId,
          }),
        ),
      ),
    [history, product, accountId],
  )

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      <div className={'margin-bottom-16'}>
        <Field
          fullWidth
          name="name"
          type="text"
          component={renderInputField}
          t={t}
          label={t('services::forms.campaign_create.fields.name.label')}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Field
          label={t('services::forms.campaign_create.fields.company.label')}
          name="company"
          component={renderSelect}
          emptyOptionLabel={t('common::select_empty_option')}
          clearOptionLabel={t('common::select_clear_option')}
          fullWidth
          t={t}
          normalize={(value = {}) => value}
          activeOptionLabelTemplate={({ name, address }) => {
            return `${name} (${address})`
          }}
          optionLabelTemplate={({ name, address }) => (
            <span>{`${name} (${address})`}</span>
          )}
          getDataMethod={async ({ searchPhrase }) => {
            if (req) {
              source.cancel()
            }

            source = CancelToken.source()

            const params = {}

            if (searchPhrase?.length) {
              params.q = searchPhrase
            }

            req = await SERVICES_API_V1.getNetworkCompany({
              networkId: 'yandex-subscription',
              cancelToken: source.token,
              params,
            })

            const { response: { data = [], pagination } = {} } = req

            return {
              pagination,
              items: data.map((item) => ({
                ...item,
                value: {
                  id: item?.id ?? null,
                  country:
                    item?.country?.geoId ??
                    item?.chainCountries?.[0]?.geoId ??
                    null,
                },
                label: item.name,
              })),
            }
          }}
          renderError={(error) => error?.id || error?.country || error}
          mode={'filter'}
          noResultsLabel={t('common::empty_results.header')}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Typography variant={'p8'} className={formStyles['legend']}>
          {t('services::forms.campaign_create.fields.product.label')}
        </Typography>

        <Field
          t={t}
          name="product"
          component={renderRadioGroup}
          options={[
            {
              value: 'GEO',
              label: t(
                'services::forms.campaign_create.fields.product.options.geo',
              ),
            },
            {
              value: 'SUBSCRIPTION',
              label: t(
                'services::forms.campaign_create.fields.product.options.subscription',
              ),
            },
            {
              value: 'WEB',
              label: t(
                'services::forms.campaign_create.fields.product.options.web',
              ),
            },
          ]}
        />
      </div>

      {product === 'WEB' && (
        <div className={'margin-bottom-16'}>
          <Field
            fullWidth
            name="url"
            type="text"
            component={renderInputField}
            t={t}
            label={t('services::forms.campaign_create.fields.url.label')}
          />
        </div>
      )}

      <div className={formStyles['formControl__action-buttons']}>
        <Button
          disabled={submitting}
          onClick={() =>
            history.push(
              pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT_CAMPAIGNS, {
                networkType,
                accountId,
              }),
            )
          }
          variant="default"
          color="text-secondary"
          size="l"
          type="button"
        >
          {t('common::form_buttons.goBack')}
        </Button>

        <Button disabled={submitting} size="l" variant="primary" type="submit">
          {t('common::form_buttons.save')}
        </Button>
      </div>
    </form>
  )
}

CampaignCreateForm = reduxForm({
  form: 'CampaignCreateForm',
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
  initialValues: {
    name: '',
    company: {
      id: null,
      country: null,
    },
    product: 'GEO',
  },
})(CampaignCreateForm)

CampaignCreateForm.propTypes = {
  accountId: PropTypes.string,
  autofill: PropTypes.func,
  invalid: PropTypes.bool,
  currentSettings: PropTypes.object,
  form: PropTypes.string,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
}

export default CampaignCreateForm
